import React, { useState, useEffect, useCallback } from 'react';
import SelectBuscable from '../components/SelectBuscable';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { hasPlanAccess, PLAN_FEATURES } from '../lib/pricing';
import UpgradeWall from '../components/UpgradeWall';
import {
  BANDAS_OCTAVA,
  calcularEstadisticaBandas,
  calcularAPVf,
  EficaciaProteccion,
  calcularNivelTotalPonderadoA,
  calcularNivelTotalPonderadoC,
  calcularPNR_HML,
  calcularPNR_SNR,
  calcularLAeqDiario,
  PeriodoExposicion,
  verificarCumplimientoAM196,
  calcularAtenuacionNivelPico,
  FrecuenciaDominanteImpulso,
  verificarLimitePico,
  textoAccionPico,
} from '../lib/ruido';
import {
  createNoiseCalculation,
  fetchNoiseCalculations,
  NoiseCalculationRow,
  NoiseCalculationInputsContinuo,
  NoiseCalculationInputsImpulsivo,
  NoiseCalculationResultadoContinuo,
  NoiseCalculationResultadoImpulsivo,
  fetchHearingProtectorCatalog,
  HearingProtectorCatalogItem,
} from '../lib/gestion-tecnica-ruido';
import { generateNoiseReportPDF } from '../lib/gestion-tecnica-ruido-pdf';
import { fetchJobPositionsFull, JobPositionLegalFields } from '../lib/gestion-tecnica-constructor';

// Mismos estilos/paleta que IluminacionMethodologyPage.tsx (fondo oscuro,
// verde para CUMPLE, ámbar para advertencias, números principales en 34px).
const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 6, color: '#e2e8f0', padding: '8px 10px', marginTop: 4,
};

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 4, color: '#e2e8f0', fontWeight: 600, fontSize: 13,
};

const smallLabelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 2, color: '#94a3b8', fontSize: 11,
};

const cardStyle: React.CSSProperties = {
  background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 16, marginBottom: 20,
};

// Datos de ejemplo verificados a mano en src/lib/ruido.ts (NTP 638 Tabla
// 2-5 y Guía INSST 2022 Apéndice 4 §4.2) — se usan como default para que el
// formulario sea probable de inmediato con resultados conocidos:
// LA≈93 dBA, LA'≈68 dBA, PNR84≈25 dB (continuo); LCpico'=119 dB(C) (impulsivo).
const LF_EJEMPLO = [85, 85, 87, 90, 90, 85, 82, 78];
const MF_EJEMPLO = [24.9, 25.4, 25.9, 27.8, 28.3, 33.2, 30.9, 40.2];
const SIGMA_EJEMPLO = [6.4, 6.1, 3.8, 2.5, 3.4, 4.9, 5.2, 4.9];

function ResultBadge({ cumple, textoLimite, textoPendiente }: { cumple: boolean; textoLimite: string; textoPendiente: string }) {
  return (
    <div>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, marginBottom: 4,
          background: cumple ? 'rgba(47,191,113,0.12)' : 'rgba(239,68,68,0.12)',
          border: `1px solid ${cumple ? 'rgba(47,191,113,0.3)' : 'rgba(239,68,68,0.3)'}`,
        }}
      >
        <strong style={{ color: cumple ? '#2FBF71' : '#ef4444', fontSize: 14 }}>
          {cumple ? 'CUMPLE' : 'NO CUMPLE'}
        </strong>
        <span style={{ color: '#94a3b8', fontSize: 12 }}>— {textoLimite}</span>
      </div>
      <span style={{ color: '#64748b', fontSize: 11 }}>{textoPendiente}</span>
    </div>
  );
}

// Grilla de 8 campos numéricos (uno por banda de octava), reutilizada para
// lecturas de ruido, mf y σ del protector — evita repetir el mismo bloque
// JSX 3 veces (8 bandas x 3 conjuntos de datos).
function GrillaBandas({
  valores, onChange, decimals = 1,
}: {
  valores: number[];
  onChange: (index: number, value: number) => void;
  decimals?: number;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
      {BANDAS_OCTAVA.map((freq, i) => (
        <div key={freq}>
          <label style={smallLabelStyle}>{freq} Hz</label>
          <input
            style={{ ...inputStyle, padding: '6px 8px', fontSize: 13 }}
            type="number"
            step={decimals === 1 ? '0.1' : '1'}
            value={valores[i]}
            onChange={e => onChange(i, parseFloat(e.target.value) || 0)}
          />
        </div>
      ))}
    </div>
  );
}

export default function RuidoMethodologyPage() {
  const { company, profile } = useCompany();
  const { impersonated }     = useImpersonation();
  const displayCompany       = impersonated ?? company;
  const companyId            = displayCompany?.id ?? '';
  const isSuperAdmin         = profile?.role === 'superadmin';

  // ── Plan gate (after all hooks, mismo criterio que Iluminación) ──
  const planAllowed = isSuperAdmin || !displayCompany || hasPlanAccess(displayCompany.plan, 'metodologia-ruido');

  const [jobPositions, setJobPositions] = useState<JobPositionLegalFields[]>([]);
  const [savedCalculations, setSavedCalculations] = useState<NoiseCalculationRow[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [descripcion, setDescripcion] = useState('');
  const [selectedJobPositionId, setSelectedJobPositionId] = useState('');

  const [tipoRuido, setTipoRuido] = useState<'continuo' | 'impulsivo'>('continuo');

  // ── Ruido continuo: lecturas por banda de octava ──
  // Cada elemento de `lecturas` es una lectura completa (8 valores, una por
  // banda). Con 1 lectura, calcularEstadisticaBandas() la usa directamente
  // como valor más probable; con 2+, promedia (uso opcional, ver ruido.ts).
  const [lecturas, setLecturas] = useState<number[][]>([[...LF_EJEMPLO]]);

  const actualizarLectura = (lecturaIdx: number, bandaIdx: number, valor: number) => {
    setLecturas(prev => prev.map((l, i) => (i === lecturaIdx ? l.map((v, j) => (j === bandaIdx ? valor : v)) : l)));
  };
  const agregarLectura = () => setLecturas(prev => [...prev, Array(BANDAS_OCTAVA.length).fill(0)]);
  const quitarLectura = (idx: number) => setLecturas(prev => prev.filter((_, i) => i !== idx));

  const [horasExposicion, setHorasExposicion] = useState(8);

  // ── Datos del protector (ficha técnica de fabricante) — compartidos entre
  // ruido continuo e impulsivo (H/M/L no se duplican, ver sección impulsiva). ──
  const [hManual, setHManual] = useState(30);
  const [mManual, setMManual] = useState(25);
  const [lManual, setLManual] = useState(20);
  const [snrManual, setSnrManual] = useState(25);
  const [protectorCatalog, setProtectorCatalog] = useState<HearingProtectorCatalogItem[]>([]);
  const [selectedProtectorId, setSelectedProtectorId] = useState('');
  const [mfPorBanda, setMfPorBanda] = useState<number[]>([...MF_EJEMPLO]);
  const [sigmaPorBanda, setSigmaPorBanda] = useState<number[]>([...SIGMA_EJEMPLO]);
  const [eficacia, setEficacia] = useState<EficaciaProteccion>(84);

  const [minutosSinProtectorPorHora, setMinutosSinProtectorPorHora] = useState(0);

  // ── Ruido impulsivo ──
  const [LCpico, setLCpico] = useState(139);
  const [frecuenciaDominante, setFrecuenciaDominante] = useState<FrecuenciaDominanteImpulso>('medias_altas');

  const [guardando, setGuardando] = useState(false);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [ultimoCalculoGuardado, setUltimoCalculoGuardado] = useState<NoiseCalculationRow | null>(null);
  const [descargandoPdf, setDescargandoPdf] = useState(false);

  const loadData = useCallback(async () => {
    if (!companyId) { setLoadingData(false); return; }
    setLoadingData(true);
    try {
      const [positions, calculations, protectors] = await Promise.all([
        fetchJobPositionsFull(companyId),
        fetchNoiseCalculations(companyId),
        fetchHearingProtectorCatalog(),
      ]);
      setJobPositions(positions);
      setSavedCalculations(calculations);
      setProtectorCatalog(protectors);
    } catch (e: any) {
      setError(e.message ?? 'Error al cargar datos');
    } finally {
      setLoadingData(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!planAllowed) {
    return <UpgradeWall {...PLAN_FEATURES['metodologia-ruido']} />;
  }

  if (loadingData) return <div style={{ padding: 24, color: '#94a3b8' }}>Cargando…</div>;

  // ===== Cálculo — ruido continuo =====
  const statsRuido = calcularEstadisticaBandas(lecturas); // mf = promedio del ruido medido por banda (o valor único)
  const Lf = statsRuido.mf;

  const APVfPorBanda = mfPorBanda.map((m, i) => calcularAPVf(m, sigmaPorBanda[i], eficacia));

  const LA = calcularNivelTotalPonderadoA(Lf, false);
  const LAp = calcularNivelTotalPonderadoA(Lf, true, APVfPorBanda); // método de bandas de octava (más preciso)

  // Origen de H/M/L/SNR: si hay un protector elegido del catálogo (y no es la
  // opción "Protector no listado"), sus valores mandan; si no, los manuales.
  const selectedProtector = protectorCatalog.find(p => p.id === selectedProtectorId);
  const H   = selectedProtectorId !== '__manual__' && selectedProtector ? selectedProtector.h_value   : hManual;
  const M   = selectedProtectorId !== '__manual__' && selectedProtector ? selectedProtector.m_value   : mManual;
  const L   = selectedProtectorId !== '__manual__' && selectedProtector ? selectedProtector.l_value   : lManual;
  const SNR = selectedProtectorId !== '__manual__' && selectedProtector ? selectedProtector.snr_value : snrManual;

  // LC para los métodos HML/SNR (que requieren un nivel C global), derivado
  // de las mismas bandas de octava con la ponderación C real del NTP 638
  // Tabla 6 (calcularNivelTotalPonderadoC) — no es una aproximación, dato no
  // capturado aparte porque este formulario no pide una medición LC directa.
  const LC = calcularNivelTotalPonderadoC(Lf);
  const PNR_HML = calcularPNR_HML(H, M, L, LC, LA);
  // NTP 638: LA' = LA - PNR (el PNR se resta del nivel LA original medido,
  // no de LC — LC solo se usa como uno de los insumos para calcular el PNR
  // en sí, vía calcularPNR_HML).
  const LAp_HML = LA - PNR_HML;
  const LAp_SNR = calcularPNR_SNR(LC, SNR);

  const periodosBase: PeriodoExposicion[] = [{ nivelDb: LAp, horas: horasExposicion }];
  const laeqDiario = calcularLAeqDiario(periodosBase, 8);
  const cumplimiento = verificarCumplimientoAM196(laeqDiario);

  // Tabla 9 NTP 638: tiempo real sin protector dentro de la jornada.
  const horasSinProtector = horasExposicion * (minutosSinProtectorPorHora / 60);
  const horasConProtector = Math.max(0, horasExposicion - horasSinProtector);
  const periodosReales: PeriodoExposicion[] = [
    { nivelDb: LAp, horas: horasConProtector },
    { nivelDb: LA, horas: horasSinProtector },
  ];
  const laeqEfectivoReal = calcularLAeqDiario(periodosReales, 8);
  const cumplimientoReal = verificarCumplimientoAM196(laeqEfectivoReal);

  // ===== Cálculo — ruido impulsivo =====
  const LCpicoAtenuado = calcularAtenuacionNivelPico(LCpico, frecuenciaDominante, H, M, L);
  // Límite con el pico atenuado; acciones con el medido sin protector (Dir. 2003/10/CE, Art. 3.2).
  const limitePico = verificarLimitePico(LCpicoAtenuado, LCpico);
  const accionPico = textoAccionPico(limitePico);

  const puedeGuardar = descripcion.trim().length > 0;

  const handleGuardar = async () => {
    setError(null);
    if (!descripcion.trim()) { setError('Ingresa una descripción del local/puesto/tarea evaluada'); return; }

    setGuardando(true);
    setGuardadoOk(false);
    try {
      let inputs: NoiseCalculationInputsContinuo | NoiseCalculationInputsImpulsivo;
      let resultado: NoiseCalculationResultadoContinuo | NoiseCalculationResultadoImpulsivo;
      let cumpleNormativa: boolean;

      if (tipoRuido === 'continuo') {
        inputs = {
          tipoRuido: 'continuo',
          lecturas, horasExposicion, H, M, L, SNR, mfPorBanda, sigmaPorBanda, eficacia, minutosSinProtectorPorHora,
        };
        resultado = {
          LA, LC, LAp, LAp_HML, LAp_SNR, laeqDiario,
          cumpleAM196: cumplimiento.cumple,
          laeqEfectivoReal,
          cumpleAM196Real: cumplimientoReal.cumple,
        };
        cumpleNormativa = cumplimiento.cumple;
      } else {
        inputs = { tipoRuido: 'impulsivo', LCpico, frecuenciaDominante, H, M, L };
        resultado = { LCpicoAtenuado, cumpleLimitePico: limitePico.cumpleLimite };
        cumpleNormativa = limitePico.cumpleLimite;
      }

      await createNoiseCalculation({
        companyId,
        jobPositionId: selectedJobPositionId || undefined,
        descripcion,
        tipoRuido,
        inputs,
        resultado,
        cumpleNormativa,
        createdBy: profile?.id ?? null,
      });
      // createNoiseCalculation() no retorna el registro insertado (mismo
      // patrón que createLightingCalculation()) — se vuelve a consultar para
      // obtener el registro recién creado con su id real, necesario para el PDF.
      const calculations = await fetchNoiseCalculations(companyId);
      setSavedCalculations(calculations);
      setUltimoCalculoGuardado(calculations[0] ?? null);
      setGuardadoOk(true);
    } catch (e: any) {
      setError(e.message ?? 'Error al guardar el cálculo');
    } finally {
      setGuardando(false);
    }
  };

  const handleDescargarPdf = async () => {
    if (!ultimoCalculoGuardado) {
      setError('No hay un cálculo guardado para generar el PDF');
      return;
    }
    setDescargandoPdf(true);
    try {
      await generateNoiseReportPDF(
        ultimoCalculoGuardado,
        companyId,
        displayCompany?.name ?? 'Empresa',
        profile?.id ?? null
      );
    } catch (e: any) {
      setError(e.message ?? 'Error al generar el PDF');
    } finally {
      setDescargandoPdf(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <h1 style={{ fontSize: 22, marginBottom: 6, color: '#e2e8f0' }}>Metodología de Ruido Ocupacional</h1>
      <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
        Cálculo según NTP 638 (INSHT, ruido continuo) y Guía Técnica INSST 2022, Apéndice 4 §4.2 (ruido impulsivo).
      </p>

      {error && (
        <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Descripción (local, puesto o tarea evaluada)</label>
        <input
          style={inputStyle}
          type="text"
          value={descripcion}
          onChange={e => setDescripcion(e.target.value)}
          placeholder="Ej. Sala de compresores, Operador de troqueladora..."
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Puesto de trabajo (opcional)</label>
        <SelectBuscable inputStyle={{ ...inputStyle, paddingRight: 32 }} ariaLabel="Puesto de trabajo (opcional)" value={selectedJobPositionId}
          onChange={setSelectedJobPositionId} vacio="— Sin asociar —" opciones={jobPositions.map(p => ({ value: p.id, label: p.name }))} />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button
          onClick={() => setTipoRuido('continuo')}
          style={{
            background: tipoRuido === 'continuo' ? '#334155' : 'transparent', color: '#e2e8f0', border: '1px solid #334155',
            borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer',
          }}
        >
          Continuo
        </button>
        <button
          onClick={() => setTipoRuido('impulsivo')}
          style={{
            background: tipoRuido === 'impulsivo' ? '#334155' : 'transparent', color: '#e2e8f0', border: '1px solid #334155',
            borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer',
          }}
        >
          Impulsivo
        </button>
      </div>

      {tipoRuido === 'continuo' && (
        <>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Nivel de ruido medido por banda de octava (dB)</label>
            {lecturas.map((lectura, idx) => (
              <div key={idx} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: idx < lecturas.length - 1 ? '1px solid #1e293b' : 'none' }}>
                {lecturas.length > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ color: '#94a3b8', fontSize: 11 }}>Lectura {idx + 1}</span>
                    <span onClick={() => quitarLectura(idx)} style={{ color: '#ef4444', fontSize: 11, cursor: 'pointer' }}>Quitar</span>
                  </div>
                )}
                <GrillaBandas valores={lectura} onChange={(bandaIdx, v) => actualizarLectura(idx, bandaIdx, v)} />
              </div>
            ))}
            <span onClick={agregarLectura} style={{ display: 'inline-block', marginTop: 4, color: '#60a5fa', fontSize: 12, cursor: 'pointer' }}>
              + Agregar otra lectura
            </span>
            {lecturas.length > 1 && (
              <div style={{ color: '#64748b', fontSize: 11, marginTop: 6 }}>
                Promediando {lecturas.length} lecturas por banda (uso opcional para ensayos propios repetidos).
              </div>
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Horas de exposición al nivel medido (dentro de una jornada de 8h)</label>
            <input
              style={{ ...inputStyle, maxWidth: 160 }}
              type="number" min={0} max={8} step="0.5"
              value={horasExposicion}
              onChange={e => setHorasExposicion(Math.min(8, Math.max(0, parseFloat(e.target.value) || 0)))}
            />
          </div>

          <div style={cardStyle}>
            <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Datos del protector auditivo (ficha técnica del fabricante)</h2>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Protector auditivo</label>
              <select style={inputStyle} value={selectedProtectorId} onChange={e => setSelectedProtectorId(e.target.value)}>
                <option value="" style={{ color: '#000000' }}>— Seleccionar —</option>
                {protectorCatalog.map(p => (
                  <option key={p.id} value={p.id} style={{ color: '#000000' }}>
                    {p.manufacturer} {p.model} (SNR {p.snr_value})
                  </option>
                ))}
                <option value="__manual__" style={{ color: '#000000' }}>Protector no listado</option>
              </select>
            </div>

            {selectedProtectorId === '__manual__' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={smallLabelStyle}>H (alta frec.)</label>
                  <input style={inputStyle} type="number" min={0} max={50} value={hManual} onChange={e => setHManual(parseFloat(e.target.value) || 0)} />
                </div>
                <div>
                  <label style={smallLabelStyle}>M (media frec.)</label>
                  <input style={inputStyle} type="number" min={0} max={50} value={mManual} onChange={e => setMManual(parseFloat(e.target.value) || 0)} />
                </div>
                <div>
                  <label style={smallLabelStyle}>L (baja frec.)</label>
                  <input style={inputStyle} type="number" min={0} max={50} value={lManual} onChange={e => setLManual(parseFloat(e.target.value) || 0)} />
                </div>
                <div>
                  <label style={smallLabelStyle}>SNR</label>
                  <input style={inputStyle} type="number" min={0} max={50} value={snrManual} onChange={e => setSnrManual(parseFloat(e.target.value) || 0)} />
                </div>
              </div>
            )}

            <label style={labelStyle}>Atenuación media por banda — mf (dB)</label>
            <div style={{ marginBottom: 12 }}>
              <GrillaBandas valores={mfPorBanda} onChange={(i, v) => setMfPorBanda(prev => prev.map((x, j) => (j === i ? v : x)))} />
            </div>

            <label style={labelStyle}>Desviación estándar por banda — σ (dB)</label>
            <div style={{ marginBottom: 12 }}>
              <GrillaBandas valores={sigmaPorBanda} onChange={(i, v) => setSigmaPorBanda(prev => prev.map((x, j) => (j === i ? v : x)))} />
            </div>

            <label style={labelStyle}>Eficacia de protección deseada</label>
            <select style={{ ...inputStyle, maxWidth: 200 }} value={eficacia} onChange={e => setEficacia(parseFloat(e.target.value) as EficaciaProteccion)}>
              {[75, 80, 84, 85, 90, 95, 99.5].map(v => (
                <option key={v} value={v} style={{ color: '#000000' }}>{v}%</option>
              ))}
            </select>
          </div>

          <div style={cardStyle}>
            <ResultBadge
              cumple={cumplimiento.cumple}
              textoLimite={`LAeq,d: ${laeqDiario.toFixed(0)} dB(A) — límite ${cumplimiento.limite} dB(A)`}
              textoPendiente="límite del Anexo 3 del AM 196 (2024), Art. 4, numeral 5, por remisión del DE 255 Art. 48"
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14, marginBottom: 14, fontSize: 13, color: '#e2e8f0' }}>
              <div>
                <span style={{ color: '#94a3b8' }}>Nivel total medido (LA, sin protector)</span>
                <div style={{ fontSize: 34, fontWeight: 600 }}>{LA.toFixed(0)} dBA</div>
              </div>
              <div>
                <span style={{ color: '#94a3b8' }}>Nivel efectivo con protector (LA', bandas de octava)</span>
                <div style={{ fontSize: 34, fontWeight: 600 }}>{LAp.toFixed(0)} dBA</div>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <span style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>
                Comparación de métodos (referencia, no alternativa a elegir — el método de bandas de octava es el más preciso)
              </span>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#e2e8f0' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #334155', textAlign: 'left' }}>
                    <th style={{ padding: '6px 8px' }}>Método</th>
                    <th style={{ padding: '6px 8px' }}>LA' (dBA)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ padding: '6px 8px' }}>Bandas de octava (más preciso)</td>
                    <td style={{ padding: '6px 8px' }}>{LAp.toFixed(0)}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ padding: '6px 8px' }}>HML</td>
                    <td style={{ padding: '6px 8px' }}>{LAp_HML.toFixed(0)}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ padding: '6px 8px' }}>SNR</td>
                    <td style={{ padding: '6px 8px' }}>{LAp_SNR.toFixed(0)}</td>
                  </tr>
                </tbody>
              </table>
              <span style={{ color: '#64748b', fontSize: 11, display: 'block', marginTop: 4 }}>
                HML y SNR usan LC = {LC.toFixed(0)} dB(C), calculado con la ponderación C (NTP 638 Tabla 6) sobre las mismas bandas de octava.
              </span>
            </div>

            <div>
              <label style={labelStyle}>
                Minutos que se quita el protector por hora: {minutosSinProtectorPorHora} min
              </label>
              <input
                type="range" min={0} max={60} step={1}
                value={minutosSinProtectorPorHora}
                onChange={e => setMinutosSinProtectorPorHora(parseInt(e.target.value, 10))}
                style={{ width: '100%' }}
              />
              <div style={{ marginTop: 8 }}>
                <span style={{ color: '#94a3b8', fontSize: 12 }}>Nivel efectivo real (con quitado de protector)</span>
                <div style={{ fontSize: 22, fontWeight: 600, color: '#e2e8f0' }}>{laeqEfectivoReal.toFixed(0)} dBA</div>
                <ResultBadge
                  cumple={cumplimientoReal.cumple}
                  textoLimite={`límite ${cumplimientoReal.limite} dB(A)`}
                  textoPendiente="mismo límite: Anexo 3 del AM 196 (2024), Art. 4, numeral 5"
                />
              </div>
            </div>
          </div>
        </>
      )}

      {tipoRuido === 'impulsivo' && (
        <>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Nivel de pico medido (LCpico, dB(C))</label>
            <input
              style={{ ...inputStyle, maxWidth: 200 }}
              type="number"
              value={LCpico}
              onChange={e => setLCpico(parseFloat(e.target.value) || 0)}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Frecuencia dominante del impulso</label>
            {([
              { value: 'bajas', label: 'Bajas', ejemplo: 'ej: explosivos, martillos' },
              { value: 'medias_altas', label: 'Medias-altas', ejemplo: 'ej: clavadoras, disparos' },
              { value: 'altas', label: 'Altas', ejemplo: 'ej: pistolas' },
            ] as { value: FrecuenciaDominanteImpulso; label: string; ejemplo: string }[]).map(opt => (
              <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="frecuenciaDominante"
                  checked={frecuenciaDominante === opt.value}
                  onChange={() => setFrecuenciaDominante(opt.value)}
                />
                <span style={{ color: '#e2e8f0', fontSize: 13 }}>{opt.label}</span>
                <span style={{ color: '#64748b', fontSize: 12 }}>— {opt.ejemplo}</span>
              </label>
            ))}
            <span style={{ color: '#64748b', fontSize: 11, display: 'block', marginTop: 4 }}>
              Clasificación orientativa (Guía INSST 2022, Apéndice 4, Tabla 12) — confirmar contra la fuente real del ruido en el puesto.
            </span>
          </div>

          <div style={{ ...cardStyle, background: '#1e293b' }}>
            <span style={{ color: '#94a3b8', fontSize: 12 }}>Usa H = {H}, M = {M}, L = {L} (mismos valores del protector ingresados en la sección Continuo)</span>
          </div>

          <div style={cardStyle}>
            <ResultBadge
              cumple={limitePico.cumpleLimite}
              textoLimite={`LCpico': ${LCpicoAtenuado.toFixed(0)} dB(C) — límite ${limitePico.limite} dB(C)`}
              textoPendiente="Directiva 2003/10/CE, Art. 3 (valor límite 140 dB(C); acción superior 137 dB(C); acción inferior 135 dB(C)), por remisión del DE 255 Art. 48"
            />
            <div style={{ marginTop: 14 }}>
              <span style={{ color: '#94a3b8' }}>Nivel de pico atenuado (LCpico')</span>
              <div style={{ fontSize: 34, fontWeight: 600, color: '#e2e8f0' }}>{LCpicoAtenuado.toFixed(0)} dB(C)</div>
            </div>
            {accionPico && (
              <div data-testid="accion-pico" style={{ marginTop: 10, color: limitePico.requiereAccionSuperior ? '#fde68a' : '#94a3b8', fontSize: 12 }}>
                {accionPico}
              </div>
            )}
          </div>
        </>
      )}

      <button
        onClick={handleGuardar}
        disabled={guardando || !puedeGuardar}
        style={{
          background: '#2FBF71', color: '#0f172a', border: 'none', borderRadius: 6,
          padding: '8px 16px', fontWeight: 600, cursor: guardando || !puedeGuardar ? 'not-allowed' : 'pointer',
          opacity: guardando || !puedeGuardar ? 0.6 : 1,
        }}
      >
        {guardando ? 'Guardando...' : 'Guardar cálculo'}
      </button>

      {guardadoOk && (
        <div style={{ color: '#2FBF71', fontSize: 13, marginTop: 10 }}>✓ Cálculo guardado correctamente</div>
      )}

      {guardadoOk && ultimoCalculoGuardado && (
        <button
          onClick={handleDescargarPdf}
          disabled={descargandoPdf}
          style={{
            background: '#3b82f6', color: '#0f172a', border: 'none', borderRadius: 6,
            padding: '8px 16px', fontWeight: 600, cursor: descargandoPdf ? 'not-allowed' : 'pointer',
            opacity: descargandoPdf ? 0.6 : 1, marginTop: 10, marginLeft: 10,
          }}
        >
          {descargandoPdf ? 'Generando PDF...' : 'Descargar reporte PDF'}
        </button>
      )}

      {savedCalculations.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 15, color: '#e2e8f0', marginBottom: 8 }}>Cálculos guardados</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#e2e8f0' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Descripción</th>
                <th style={{ padding: '6px 8px' }}>Tipo</th>
                <th style={{ padding: '6px 8px' }}>Cumple</th>
                <th style={{ padding: '6px 8px' }}>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {savedCalculations.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '6px 8px' }}>{c.descripcion}</td>
                  <td style={{ padding: '6px 8px' }}>{c.tipo_ruido}</td>
                  <td style={{ padding: '6px 8px' }}>{c.cumple_normativa ? '✓' : '✗'}</td>
                  <td style={{ padding: '6px 8px' }}>{new Date(c.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
